import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Send, MessageSquare } from 'lucide-react'
import { titleCase, formatPrice, type ListingDetail } from '../../lib/jlApi'

export type InquiryMode = 'ask' | 'showing'

interface ListingInquiryModalProps {
  listing: ListingDetail | null
  mode: InquiryMode
  onClose: () => void
}

// Team contacts (PARITY_V2 C.2 — verified). No form backend yet: submit opens
// the visitor's mail client addressed to the team. TODO-CLIENT: wire a real
// endpoint (worker mailroute / web3forms key) before launch.
const TEAM_EMAIL = 'shawn@jacklingo.com'
const TEAM_CELL_DIGITS = '3025428591' // Shawn's verified cell
const TEAM_PHOTO = '/media/team/shawn-mcdonnell-800.jpg'

const TIME_SLOTS = [
  'Morning (9am–12pm)',
  'Afternoon (12–4pm)',
  'Evening (4–7pm)',
]

// Next 10 calendar days as {value,label} for the showing date selects.
function upcomingDays(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 1; i <= 10; i++) {
    const d = new Date(now)
    d.setDate(now.getDate() + i)
    const value = d.toISOString().slice(0, 10)
    const label = d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    out.push({ value, label })
  }
  return out
}

export default function ListingInquiryModal({
  listing,
  mode,
  onClose,
}: ListingInquiryModalProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [day1, setDay1] = useState('')
  const [time1, setTime1] = useState(TIME_SLOTS[0])
  const [day2, setDay2] = useState('')
  const [time2, setTime2] = useState(TIME_SLOTS[1])
  const [submitted, setSubmitted] = useState(false)

  const open = listing !== null
  const days = upcomingDays()

  const address = listing
    ? `${titleCase(listing.street)}, ${titleCase(listing.city)}, ${listing.state}`
    : ''
  const mls = listing?.listingId ?? ''
  const title = mode === 'showing' ? 'Request a showing' : 'Ask about this home'
  const subjectPrefix = mode === 'showing' ? 'Showing Request' : 'Listing Inquiry'

  const smsBody = `Hi Shawn & Merritt, I'm interested in ${address}${mls ? ` (MLS ${mls})` : ''}. `
  // iOS uses `?&body=`; Android uses `?body=`. `?&body=` works on both.
  const smsHref = `sms:${TEAM_CELL_DIGITS}?&body=${encodeURIComponent(smsBody)}`

  const reset = () => {
    setName('')
    setEmail('')
    setPhone('')
    setMessage('')
    setDay1('')
    setDay2('')
    setSubmitted(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!listing) return

    const dayLabel = (v: string) =>
      days.find((d) => d.value === v)?.label ?? v
    const showingLines =
      mode === 'showing'
        ? `\n\nPreferred times:\n1) ${day1 ? dayLabel(day1) : '(any)'} — ${time1}` +
          (day2 ? `\n2) ${dayLabel(day2)} — ${time2}` : '')
        : ''

    const fullMessage =
      `${message || `I'd like more information about ${address}.`}` +
      showingLines +
      `\n\n— Sent from the debeachrealestate.com property browser` +
      `\nProperty: ${address}` +
      (mls ? `\nMLS #: ${mls}` : '') +
      (listing.priceDisplay || listing.price
        ? `\nList price: ${listing.priceDisplay || formatPrice(listing.price)}`
        : '') +
      `\n\nFrom: ${name} <${email}>${phone ? ` · ${phone}` : ''}`

    const subject = `${subjectPrefix} — ${address}${mls ? ` (MLS ${mls})` : ''}`
    window.location.href = `mailto:${TEAM_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullMessage)}`
    setSubmitted(true)
  }

  const inputClass =
    'w-full rounded-lg border border-[var(--dune)] bg-white px-3 py-2.5 text-base text-[var(--navy)] placeholder:text-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]'
  const selectClass =
    'w-full appearance-none rounded-lg border border-[var(--dune)] bg-white px-3 py-2.5 text-sm text-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]'

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[19500] bg-black/50"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="fixed inset-x-0 bottom-0 z-[19600] flex max-h-[92vh] flex-col rounded-t-3xl bg-white shadow-2xl sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-h-[88vh] sm:w-[440px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
          >
            <div className="flex items-center justify-between border-b border-[var(--sand)] px-4 py-3">
              <span className="font-display text-base font-semibold uppercase tracking-[0.1em] text-[var(--navy)]">
                {title}
              </span>
              <button
                onClick={close}
                aria-label="Close"
                className="p-1 text-[var(--slate)] active:text-[var(--navy)]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {submitted ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <img
                    src={TEAM_PHOTO}
                    alt="Shawn McDonnell"
                    className="mb-4 h-20 w-20 rounded-full object-cover shadow"
                  />
                  <h3 className="font-display text-xl uppercase tracking-[0.1em] text-[var(--navy)]">
                    Draft opened in your mail app
                  </h3>
                  <p className="mt-1 text-sm text-[var(--slate)]">
                    Hit send and we&rsquo;ll reply within 24 hours. &mdash; Shawn &amp; Merritt
                  </p>
                  <button
                    onClick={close}
                    className="mt-6 rounded-lg bg-[var(--navy)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--gold)]"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  {listing && (
                    <div className="rounded-lg bg-[var(--cream)] px-3 py-2 text-sm text-[var(--navy)]">
                      <span className="font-medium">{address}</span>
                      {mls && (
                        <span className="text-[var(--slate)]"> · MLS# {mls}</span>
                      )}
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={(e) =>
                      e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }
                    className={inputClass}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={(e) =>
                      e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }
                    className={inputClass}
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onFocus={(e) =>
                      e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }
                    className={inputClass}
                  />

                  {mode === 'showing' && (
                    <div className="space-y-2 rounded-lg border border-[var(--sand)] p-3">
                      <p className="text-sm font-medium text-[var(--navy)]">
                        Preferred times
                      </p>
                      <div className="flex gap-2">
                        <select
                          value={day1}
                          onChange={(e) => setDay1(e.target.value)}
                          className={selectClass}
                          aria-label="Preferred date 1"
                        >
                          <option value="">Pick a day</option>
                          {days.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={time1}
                          onChange={(e) => setTime1(e.target.value)}
                          className={selectClass}
                          aria-label="Preferred time 1"
                        >
                          {TIME_SLOTS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={day2}
                          onChange={(e) => setDay2(e.target.value)}
                          className={selectClass}
                          aria-label="Preferred date 2"
                        >
                          <option value="">Alternate day (optional)</option>
                          {days.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={time2}
                          onChange={(e) => setTime2(e.target.value)}
                          className={selectClass}
                          aria-label="Preferred time 2"
                        >
                          {TIME_SLOTS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <textarea
                    placeholder={
                      mode === 'showing'
                        ? 'Anything we should know? (optional)'
                        : 'Your question (optional)'
                    }
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onFocus={(e) =>
                      e.currentTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }
                    className={`${inputClass} h-24 resize-none`}
                  />

                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--navy)] py-3.5 text-sm font-semibold text-white transition hover:bg-[var(--gold)] active:scale-[0.98]"
                  >
                    <Send size={16} />
                    {mode === 'showing' ? 'Request showing' : 'Send to Shawn & Merritt'}
                  </button>

                  <a
                    href={smsHref}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--dune)] py-3 text-sm font-medium text-[var(--navy)] transition hover:bg-[var(--sand)] active:scale-[0.98]"
                  >
                    <MessageSquare size={16} />
                    Or text the team now
                  </a>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
